import { BottomTabBarHeightContext } from '@react-navigation/bottom-tabs';
import { Box, Button, HStack, IBoxProps } from 'native-base';
import { ReactNode, useContext } from 'react';
import { Linking, Platform } from 'react-native';

import { DEFAULT_PADDING } from 'app/components/ResponsiveContainer';
import useAuth from 'app/hooks/useAuth';
import useDestinations from 'app/hooks/useDestinations';
import useEffectiveBottomInset from 'app/hooks/useEffectiveBottomInset';
import useGuest from 'app/hooks/useGuest';
import useRootNavigation from 'app/hooks/useRootNavigation';
import useStore, { PurchasesReturnTarget } from 'app/hooks/useStore';
import { getCurrentRoute, getCurrentRouteParams } from 'app/navigation/navigationHelper';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { webProduct } from 'app/services/LinkHelper';
import { tID } from 'app/services/TestHelper';

const VERTICAL_PADDING = 3;

export type LinkTicketsPromptProps = Pick<IBoxProps, 'bottom' | 'position' | 'top' | 'zIndex'> & {
  productContentKey?: string;
  children?: ReactNode;
  safeInsetBottom?: boolean;
  alwaysShow?: boolean;
};

export default function LinkTicketsPrompt({
  safeInsetBottom: _safeInsetBottom,
  productContentKey,
  alwaysShow,
  ...props
}: LinkTicketsPromptProps) {
  const navigation = useRootNavigation();
  const effectiveBottomInset = useEffectiveBottomInset();
  const tabBarHeight = useContext(BottomTabBarHeightContext);
  const { t, locale } = useI18n();
  const { isVerified } = useAuth();
  const { hasEntitlements, entitlements } = useGuest();
  const setPurchasesReturnTarget = useStore((s) => s.setPurchasesReturnTarget);
  const { data: destinations } = useDestinations({ enabled: !!productContentKey });
  const productEntitlement = entitlements.find(
    (ent) => ent.productContentKey === productContentKey
  );
  const viewMyPurchasesLabel = t('gen_view_my_purchases') || 'View My Purchases';

  if (!alwaysShow && (productContentKey ? productEntitlement : hasEntitlements)) return null;

  const handleLinkTickets = () => {
    const event = isVerified ? Events.NavViewMyPurchases : Events.NavLinktickets;

    Analytics.trackEvent(event, getNavEventProps({ navigation, productContentKey }));
    const returnTarget = getPurchasesReturnTarget({ navigation, productContentKey }) ?? null;

    if (isVerified) {
      setPurchasesReturnTarget(returnTarget);
      navigation.navigate('PurchasesStack', {
        screen: 'PurchasesList',
      });
    } else {
      navigation.navigate('Authentication');
    }
  };
  const handleBuyNow = () => {
    Analytics.trackEvent(Events.NavBuyNow, getNavEventProps({ navigation, productContentKey }));
    const productSlug =
      destinations?.find((product) => product.key === productContentKey)?.slug ?? '';
    Linking.openURL(webProduct({ locale, productSlug }));
  };

  // Android: if the tab bar isn't constraining layout (or caller explicitly requests), pad to keep buttons above the system nav bar.
  const tabBarConstrainsContent = tabBarHeight != null && tabBarHeight > 0;
  const shouldApplyBottomInset =
    Platform.OS === 'android' && (_safeInsetBottom ?? !tabBarConstrainsContent);
  const bottomInset = shouldApplyBottomInset ? effectiveBottomInset : 0;

  return (
    <Box
      bgColor="brand.secondary"
      color="white"
      position="relative"
      testID={tID('LinkTicketsPrompt')}
      w="full"
      {...props}
    >
      <Box px={DEFAULT_PADDING} py={VERTICAL_PADDING}>
        <HStack alignItems="center" flexDirection="row" justifyContent="space-evenly" space="4">
          <Button testID={tID('BuyNowButton')} variant="linkInverted" onPress={handleBuyNow}>
            {t('gen_buy_now')}
          </Button>

          <Button
            colorScheme="primary"
            maxW="65%"
            testID={tID(isVerified ? 'ViewMyPurchasesButton' : 'AddYourTicketsButton')}
            onPress={handleLinkTickets}
          >
            {isVerified ? viewMyPurchasesLabel : t('gen_link_tickets')}
          </Button>
        </HStack>
      </Box>

      {!!bottomInset && (
        <Box h={`${bottomInset}px`} testID={tID('LinkTicketsPrompt-bottomInset')} />
      )}
    </Box>
  );
}

function getNavEventProps({
  navigation,
  productContentKey,
}: {
  navigation: ReturnType<typeof useRootNavigation>;
  productContentKey: Maybe<string>;
}) {
  const params = getCurrentRouteParams(navigation);
  const productId = params?.productId ?? productContentKey;

  if (!productId) return undefined;

  return {
    productId,
    ...(params?.attractionId && {
      attractionId: params?.attractionId,
    }),
  };
}

function getPurchasesReturnTarget({
  navigation,
  productContentKey,
}: {
  navigation: ReturnType<typeof useRootNavigation>;
  productContentKey: Maybe<string>;
}): PurchasesReturnTarget | undefined {
  const route = getCurrentRoute(navigation);
  const params = getCurrentRouteParams(navigation);
  const productId = params?.productId ?? productContentKey;
  const attractionId = params?.attractionId;

  switch (route?.name) {
    case 'Menu':
    case 'Destinations':
    case 'Notifications':
      return { type: route.name };
    case 'Product':
      return productId ? { type: 'Product', productId } : undefined;
    case 'AttractionTickets':
      return productId ? { type: 'AttractionTickets', productId } : undefined;
    case 'Attraction':
      return productId && attractionId
        ? { type: 'Attraction', productId, attractionId }
        : undefined;
    default:
      return undefined;
  }
}
